import { useState, useContext, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useForm } from '../hooks/useForm'
import { submitMatchUpload } from '../services/uploadService'
import { handleApiError } from '../utils/errorHandler'
import { MATCH_TYPES, FOCUS_TYPES, SUCCESS_MESSAGES, PRICING_PLANS, calculatePrice, formatPrice } from '../config/constants'
import { ToastContext } from '../App'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Button from '../components/ui/Button'
import Tooltip from '../components/ui/Tooltip'
import PaymentFlow from '../components/PaymentFlow'

export default function Upload() {
  const toast = useContext(ToastContext)
  const {
    values: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validate,
    reset,
  } = useForm({
    name: '',
    email: '',
    phone: '',
    driveLink: '',
    matchType: '',
    focus: '',
    videoHours: '',
    planId: '',
    additionalNotes: '',
  })

  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionId, setSubmissionId] = useState(null)
  const [orderId, setOrderId] = useState(null)
  const [showPayment, setShowPayment] = useState(false)
  const [calculatedPrice, setCalculatedPrice] = useState(0)

  // Calculate price when plan or hours change
  useEffect(() => {
    if (formData.planId && formData.videoHours) {
      const hours = parseFloat(formData.videoHours) || 0
      if (hours > 0) {
        const price = calculatePrice(formData.planId, hours)
        setCalculatedPrice(price)
      } else {
        setCalculatedPrice(0)
      }
    } else {
      setCalculatedPrice(0)
    }
  }, [formData.planId, formData.videoHours])

  const matchTypeOptions = [
    { value: MATCH_TYPES.TURF, label: MATCH_TYPES.TURF },
    { value: MATCH_TYPES.T20, label: MATCH_TYPES.T20 },
    { value: MATCH_TYPES.ODI, label: MATCH_TYPES.ODI },
    { value: MATCH_TYPES.TEST, label: MATCH_TYPES.TEST },
    { value: MATCH_TYPES.GULLY, label: MATCH_TYPES.GULLY },
    { value: MATCH_TYPES.OTHER, label: MATCH_TYPES.OTHER },
  ]

  const focusOptions = [
    { value: FOCUS_TYPES.BATTING, label: FOCUS_TYPES.BATTING },
    { value: FOCUS_TYPES.BOWLING, label: FOCUS_TYPES.BOWLING },
    { value: FOCUS_TYPES.ALL_ROUNDER, label: FOCUS_TYPES.ALL_ROUNDER },
    { value: FOCUS_TYPES.FIELDING, label: FOCUS_TYPES.FIELDING },
    { value: FOCUS_TYPES.TEAM, label: FOCUS_TYPES.TEAM },
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!validate()) {
      toast?.error('Please fix the errors in the form')
      return
    }

    setIsSubmitting(true)

    try {
      const result = await submitMatchUpload(formData)

      if (result.success) {
        // Store submission ID for payment flow
        if (result.data?.submissionId) {
          setSubmissionId(result.data.submissionId)
        }
        // Show payment flow instead of success message
        setShowPayment(true)
        toast?.success('Form submitted! Please proceed to payment.')
        // If confirmation email wasn't sent, show API error so you can fix (e.g. check Railway logs)
        const emailStatus = result.data?.emailStatus
        if (emailStatus && !emailStatus.userSent) {
          const reason = emailStatus.errors?.[0] || 'Email service unavailable'
          toast?.error(`Submission saved, but confirmation email failed: ${reason}. Save your submission ID.`, { duration: 10000 })
        }
      } else {
        const errorMessage = handleApiError(result.error)
        toast?.error(errorMessage)
      }
    } catch (error) {
      const errorMessage = handleApiError(error)
      toast?.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show payment flow after form submission
  if (showPayment && submissionId) {
    return (
      <PaymentFlow
        submissionId={submissionId}
        planId={formData.planId}
        videoHours={parseFloat(formData.videoHours)}
        calculatedPrice={calculatedPrice}
        onPaymentSuccess={(paidOrderId) => {
          setOrderId(paidOrderId ?? null)
          setSubmitted(true)
          setShowPayment(false)
        }}
        onBack={() => {
          setShowPayment(false)
        }}
      />
    )
  }

  // Show success message after payment
  if (submitted) {
    return (
      <div className="pt-24 pb-20 px-4 min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-2xl mx-auto"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 10 }}
            className="text-8xl mb-6"
          >
            🔥
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            <span className="text-white">YOUR MATCH IS GOING TO </span>
            <span className="text-pitch-green text-glow">LOOK INSANE</span>
          </h2>
          <p className="text-xl text-gray-400 mb-6">
            Payment successful! We've received your match and payment. Our team will start editing and get back to you soon.
          </p>
          <div className="space-y-3 mb-6">
            {submissionId && (
              <div className="bg-pitch-green/10 border border-pitch-green/30 rounded-lg p-4">
                <p className="text-pitch-green text-sm font-semibold mb-1">Submission ID:</p>
                <p className="text-gray-300 font-mono text-sm break-all">{submissionId}</p>
              </div>
            )}
            {orderId && (
              <div className="bg-pitch-green/10 border border-pitch-green/30 rounded-lg p-4">
                <p className="text-pitch-green text-sm font-semibold mb-1">Order ID:</p>
                <p className="text-gray-300 font-mono text-sm break-all">{orderId}</p>
                <p className="text-gray-500 text-xs mt-2">Save both IDs for reference</p>
              </div>
            )}
          </div>
          <p className="text-gray-500 mb-6">
            Check your email for confirmation and next steps.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false)
              reset()
              setSubmissionId(null)
              setOrderId(null)
              setCalculatedPrice(0)
            }}
            variant="outline"
          >
            Submit Another Match
          </Button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="pt-24 pb-20 px-4 min-h-screen">
      <div className="container mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            <span className="text-white">UPLOAD YOUR </span>
            <span className="text-pitch-green text-glow">MATCH</span>
          </h1>
          <p className="text-xl text-gray-400">
            Paste your Google Drive link and let's create magic
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-2xl p-8 md:p-12"
        >
          <div className="space-y-6">
            <Input
              label="Name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && errors.name}
              required
              placeholder="Your name"
            />

            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email}
              required
              placeholder="your.email@example.com"
            />

            <Input
              label="Phone Number"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.phone && errors.phone}
              required
              placeholder="+91 9876543210"
            />

            <div>
              <div className="mb-2 flex items-center gap-2">
                <label htmlFor="driveLink" className="block text-pitch-green font-bold">
                  Google Drive Link <span className="text-red-500">*</span>
                </label>
                <Tooltip
                  position="right"
                  content={
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <span className="text-pitch-green text-lg flex-shrink-0 mt-0.5">✓</span>
                        <span className="flex-1">Provide video in <strong className="text-white">best quality</strong> (1080p or higher) for best results</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-pitch-green text-lg flex-shrink-0 mt-0.5">✓</span>
                        <span className="flex-1">Video will be processed based on your <strong className="text-white">selected pricing package</strong></span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-pitch-green text-lg flex-shrink-0 mt-0.5">✓</span>
                        <span className="flex-1">Ensure the link is <strong className="text-white">publicly accessible</strong> or shared with edit permissions</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-pitch-green text-lg flex-shrink-0 mt-0.5">✓</span>
                        <span className="flex-1">Supported formats: <strong className="text-white">MP4, MOV, AVI</strong> (MP4 recommended)</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-pitch-green text-lg flex-shrink-0 mt-0.5">✓</span>
                        <span className="flex-1">Upload the <strong className="text-white">complete match recording</strong> - we'll extract the highlights</span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-pitch-green text-lg flex-shrink-0 mt-0.5">✓</span>
                        <span className="flex-1">Make sure audio is <strong className="text-white">clear and audible</strong> for better editing</span>
                      </div>
                    </div>
                  }
                >
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-pitch-green/20 border border-pitch-green/50 hover:bg-pitch-green/30 transition-colors">
                    <svg
                      className="w-3.5 h-3.5 text-pitch-green"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </Tooltip>
              </div>
              <input
                id="driveLink"
                name="driveLink"
                type="url"
                value={formData.driveLink}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="https://drive.google.com/file/d/..."
                required
                className={`
                  w-full bg-gray-900 border rounded-lg px-4 py-3 text-white
                  focus:outline-none transition-colors
                  ${touched.driveLink && errors.driveLink ? 'border-red-500' : 'border-pitch-green/30 focus:border-pitch-green'}
                `}
              />
              {touched.driveLink && errors.driveLink && (
                <p className="mt-1 text-sm text-red-500">{errors.driveLink}</p>
              )}
              <div className="mt-2 space-y-1">
                <p className="text-gray-500 text-sm">
                  Make sure the link is publicly accessible or shared with us
                </p>
                <a
                  href="https://support.google.com/drive/answer/2494822"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pitch-green hover:text-neon-green text-xs flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  How to share Google Drive files
                </a>
              </div>
            </div>

            <Select
              label="Match Type"
              name="matchType"
              value={formData.matchType}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.matchType && errors.matchType}
              required
              placeholder="Select match type"
              options={matchTypeOptions}
            />

            <Select
              label="Focus"
              name="focus"
              value={formData.focus}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.focus && errors.focus}
              required
              placeholder="What should we highlight?"
              options={focusOptions}
            />

            <div className="bg-pitch-green/5 border border-pitch-green/20 rounded-lg p-4">
              <h3 className="text-pitch-green font-bold text-sm mb-3">💰 Pricing & Duration</h3>
              
              <Select
                label="Select Package"
                name="planId"
                value={formData.planId}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.planId && errors.planId}
                required
                placeholder="Choose your package"
                options={PRICING_PLANS.map(plan => ({
                  value: plan.id,
                  label: `${plan.name} - ₹${plan.pricePerHour.toLocaleString('en-IN')}/hour`,
                }))}
              />

              <div className="mt-4">
                <Input
                  label="Video Duration (Hours)"
                  name="videoHours"
                  type="number"
                  value={formData.videoHours}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.videoHours && errors.videoHours}
                  required
                  placeholder="e.g., 2.5"
                  min="0.5"
                  step="0.5"
                />
                <p className="text-gray-500 text-xs mt-2">
                  Enter the duration of your match video in hours (e.g., 2.5 for 2 hours 30 minutes)
                </p>
              </div>

              {calculatedPrice > 0 && (
                <div className="mt-4 p-3 bg-pitch-green/10 border border-pitch-green/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 text-sm">Total Amount:</span>
                    <span className="text-pitch-green text-2xl font-black">
                      {formatPrice(calculatedPrice)}
                    </span>
                  </div>
                  {formData.planId && formData.videoHours && (
                    <p className="text-gray-500 text-xs mt-1 text-right">
                      {PRICING_PLANS.find(p => p.id === formData.planId)?.pricePerHour.toLocaleString('en-IN')} × {formData.videoHours} hours
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Additional Notes <span className="text-gray-500 font-normal">(Optional)</span>
              </label>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={4}
                placeholder="Any specific moments, players, or instructions you'd like us to focus on..."
                className="w-full px-4 py-3 bg-gray-900 border-2 border-pitch-green/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-pitch-green transition-colors resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              size="lg"
              className="w-full"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Match'}
            </Button>
          </div>
        </motion.form>
      </div>
    </div>
  )
}
